import type { InboxInsuredDetailFragment } from "@qw/graphql";
import { ColumnFooter, HoverCard, HoverCardContent, HoverCardTrigger } from "@qw/ui";
import { PRODUCTION_APP_ORIGINS } from "@/constants/app-origins";
import { useCreateDraft } from "@/providers/use-create-draft";

type InsuredAgent = InboxInsuredDetailFragment["agent"];

/**
 * Below this the footer drops to its one-row form. The merged rail's panel
 * floors at 16rem and the header and tabs already spend ~88px of it, so the
 * three-line footer's ~76px would leave the plan list under 100px. Above this
 * there is room for the extra lines.
 */
const EXPANDED_MIN_RAIL_HEIGHT = 320;

/**
 * Phone coverage on `UserAccount` is under 5%, so the phone line renders only
 * when there is a number and reserves no slot when there is not.
 *
 * The email composes in Inbox rather than being a `mailto:` — handing an email
 * client's own compose action off to the OS mail app would be a strange thing to
 * do, and the draft comes back linked to the insured whose rail it was started
 * from, which a `mailto:` cannot do.
 */
const AgentContactLines = ({ agent, insuredId }: { agent: InsuredAgent; insuredId: string | null | undefined }) => {
  const [createDraft, { loading }] = useCreateDraft();

  return (
    <div className="text-2xs text-muted-foreground">
      <div className="truncate">
        <a
          href={`${PRODUCTION_APP_ORIGINS.brokerage}/agency/${agent.agencyId}`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
        >
          {agent.agency.name}
        </a>
      </div>
      <div className="truncate">
        <button
          type="button"
          // Without this a second click while the mutation is in flight leaves the
          // broker with two empty drafts and no way to tell which one opened.
          disabled={loading}
          onClick={() => createDraft({ insuredId: insuredId ?? undefined, to: [agent.email] })}
          className="max-w-full truncate hover:underline disabled:opacity-70"
        >
          {agent.email}
        </button>
      </div>
      {agent.phone && (
        <div className="truncate">
          <a href={`tel:${agent.phone}`} className="hover:underline">
            {agent.phone}
          </a>
        </div>
      )}
    </div>
  );
};

/**
 * The rail's agent footer, mirroring Web's `insured-agency.tsx`. Web collapses
 * on a sidebar-open flag because its constraint is width; the rail's constraint
 * is height, so this collapses on the rail's measured height instead — and the
 * collapsed form has to actually give that height back, which is why it drops to
 * `ColumnFooter`'s one-gutter row and defers the rest to a hover card.
 *
 * This owns its `ColumnFooter` rather than being placed inside one, so an
 * insured with no agent leaves no empty footer behind.
 */
export const InsuredAgentCard = ({
  agent,
  insuredId,
  railHeight,
}: {
  agent: InsuredAgent | null | undefined;
  insuredId: string | null | undefined;
  railHeight: number | null;
}) => {
  if (!agent) {
    return null;
  }

  const agentName = `${agent.firstName} ${agent.lastName}`;

  if (railHeight !== null && railHeight < EXPANDED_MIN_RAIL_HEIGHT) {
    return (
      <ColumnFooter className="justify-start">
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              aria-label={`Assigned agent: ${agentName}`}
              className="font-medium max-w-full text-xs truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {agentName}
            </button>
          </HoverCardTrigger>
          <HoverCardContent side="top" align="start" className="p-3 w-64">
            <div className="font-medium mb-1 text-xs truncate">{agentName}</div>
            <AgentContactLines agent={agent} insuredId={insuredId} />
          </HoverCardContent>
        </HoverCard>
      </ColumnFooter>
    );
  }

  return (
    <ColumnFooter className="h-auto justify-start py-2">
      <div className="flex-auto min-w-0">
        <div className="font-medium text-xs truncate">{agentName}</div>
        <AgentContactLines agent={agent} insuredId={insuredId} />
      </div>
    </ColumnFooter>
  );
};
